import { useEffect, useState } from "react";
import Alert from "src/components/Alert";
import Input from "src/components/form/Input";
import Form from "src/components/forms/TaskForm/Form";
import AILoading from "src/components/forms/TaskForm/elements/AILoading";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import useTaskForm from "src/hooks/api/tasks/useTaskForm";

const DURATION = 4000;

interface AIQuestionsFormProps extends Translation {
	hideButtons?: boolean;
}

const AIQuestionsForm = ({ t, hideButtons }: AIQuestionsFormProps) => {
	const { task, actions, current, questions } = useTaskForm();
	const [loading, setLoading] = useState(true);
	const [tries, setTries] = useState(0);

	useEffect(() => {
		setLoading(true);
		let id: NodeJS.Timeout | null = null;
		const getQuestions = () => {
			if (tries > 3) {
				setLoading(false);
				return;
			}
			actions
				.getQuestions()
				.then((res) => {
					setLoading(false);
				})
				.catch(() => {
					setTries((count) => count + 1);

					id = setTimeout(() => {
						getQuestions();
					}, DURATION);
				});
		};

		if (task.answers?.length) {
			actions.setQuestions(task.answers);
			setLoading(false);
		} else {
			getQuestions();
		}

		return () => {
			if (id) {
				clearTimeout(id);
			}
			actions.clearQuestions();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleChange = (value: string, question: string) => {
		if (task?.answers?.length) {
			actions.set({
				answers: task.answers.map((item: any) => {
					if (item.question === question) {
						return {
							...item,
							answer: value,
						};
					}
					return item;
				}),
			});
			return;
		}

		actions.set({
			answers: questions.map((item: any) => {
				if (item.question === question) {
					return {
						...item,
						answer: value,
					};
				}
				return item;
			}),
		});
	};

	const getNextDisabled = () => {
		return (
			!task.answers?.length ||
			!(task?.answers || []).every((item: any) => item.answer)
		);
	};

	if (loading) {
		return <AILoading description={t("loading")} />;
	}

	return (
		<Form
			{...{ hideButtons }}
			onSubmit={async () => {
				actions.updateCurrent(current + 1);
			}}
			value={{
				answers: task.answers,
			}}
			nextDisabled={getNextDisabled()}
		>
			{() => {
				return (
					<>
						<Alert type="info">
							<p>{t("disclaimer")}</p>
						</Alert>

						{(questions || []).map(
							(question: any, index: number) => {
								return (
									<Input
										key={question.question}
										// multiline
										autoFocus={index === 0}
										label={question.question}
										placeholder=" "
										name="answer"
										value={
											task?.answers?.find(
												(item: any) =>
													item.question ===
													question.question
											)?.answer
										}
										handleChange={(e) => {
											handleChange(
												e.target.value,
												question.question
											);
										}}
										// {...{ handleChange }}
									/>
								);
							}
						)}
					</>
				);
			}}
		</Form>
	);
};

AIQuestionsForm.locale = {
	nl: {
		loading: "De vragen worden gegenereerd",
		disclaimer:
			"Op basis van deze vragen maakt onze AI een opdrachtomschrijving, deze kan je nog aanpassen",
	},
	en: {
		loading: "The questions are being generated",
		disclaimer:
			"Our AI will create a task description based on your answers, the description can be edited before publishing",
	},
};

export default withTranslation(AIQuestionsForm);
